import { useCallback, useEffect, useState } from 'react';
import {
  getAppointmentIframeAllowedOrigins,
  isMockAppointmentIframeEnabled,
} from 'common/env';

export const BOOKING_MESSAGE_TYPES = {
  /** Fired when the user successfully completes a booking. */
  BOOKING_COMPLETED: 'BOOKING_COMPLETED',
  /** Fired when the user completes a booking and should be returned home. */
  BOOKING_COMPLETED_GO_HOME: 'BOOKING_COMPLETED_GO_HOME',
} as const;

type BookingMessageType =
  (typeof BOOKING_MESSAGE_TYPES)[keyof typeof BOOKING_MESSAGE_TYPES];

export type BookingFlowStatus = 'pending' | 'completed' | 'completed-go-home';

function isBookingMessage(data: unknown): data is { type: BookingMessageType } {
  if (typeof data !== 'object' || data === null) return false;
  const { type } = data as { type?: unknown };
  return (
    type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED ||
    type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED_GO_HOME
  );
}

export function useIframeFlowStatus(): { status: BookingFlowStatus } {
  const [status, setStatus] = useState<BookingFlowStatus>('pending');

  useEffect(() => {
    if (import.meta.env.DEV && !isMockAppointmentIframeEnabled()) {
      const origins = getAppointmentIframeAllowedOrigins();
      if (origins.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[patient-portal] useIframeFlowStatus: VITE_APPOINTMENT_IFRAME_ALLOWED_ORIGINS is ' +
            'not configured. All inbound postMessage events will be blocked. ' +
            'Set this variable to the iframe origin(s) to enable the booking flow.',
        );
      }
    }
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    const allowedOrigins = getAppointmentIframeAllowedOrigins();
    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    if (!isBookingMessage(event.data)) {
      return;
    }

    if (event.data.type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED) {
      setStatus('completed');
      return;
    }

    if (event.data.type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED_GO_HOME) {
      setStatus('completed-go-home');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  return { status };
}
